import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslate } from "helpers";

import { TireRowProps } from "./types";
import { FleetTireAddRow } from "services/api/fleet/tires/types";
import { deleteFleetTireAPI } from "services/api/fleet/tires/api";
import { validateYup } from "services/forms/validation/validation";
import { TIRE_SET_TIRE_EDIT_FORM_SCHEMA } from "./validation";

import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import TireAddModal from "ui/modules/fleet/tires/pages/TireAddModal/TireAddModal";

import { useTireDropdownOptions } from "../../hooks/useTireDropdownOptions";
import { groupByDimensions } from "../../helpers";
import TireTable from "../TireTable";
import TireEmptyState from "../TireEmptyState";

const TireRow: React.FC<TireRowProps> = (props) => {
    const { formValues, setFormValues, errors, setErrors, fetchTireSet, registerValidator } = props;

    const { t } = useTranslate();

    const [tireId, setTireId] = useState<string>("");
    const [showTireAddModal, setShowTireAddModal] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());

    const {
        tireTypes, tireManufacturers,
        tireTypeOptions, manufacturerOptions,
    } = useTireDropdownOptions();

    const MAX_TIRES = 4;
    const groups = useMemo(() => groupByDimensions(formValues.tires), [formValues.tires]);
    const allSameDimensions = groups.length <= 1;
    const remaining = MAX_TIRES - formValues.tires.length;

    useEffect(() => {
        registerValidator(validateStep);
    }, [formValues]);

    const validateStep = () => {
        if (!setErrors) return true;
        return validateYup(formValues, TIRE_SET_TIRE_EDIT_FORM_SCHEMA, setErrors);
    };

    const handleGroupChange = useCallback((indices: number[], field: string, value: any) => {
        setDirtyFields((prev) => new Set(prev).add(field));
        setFormValues((prev) => {
            const newTires = [...prev.tires];
            for (const i of indices) {
                if (field === "width" || field === "height" || field === "radius") {
                    newTires[i] = { ...newTires[i], [field]: value };
                } else if (field === "tireTypeId") {
                    newTires[i] = { ...newTires[i], tireType: tireTypes.find(t => t.id === value) ?? null };
                } else if (field === "tireManufacturerId") {
                    newTires[i] = { ...newTires[i], tireManufacturer: tireManufacturers.find(m => m.id === value) ?? null };
                } else if (field === "dot" || field === "profileDepth") {
                    newTires[i] = { ...newTires[i], [field]: value };
                } else if (field === "rof") {
                    newTires[i] = { ...newTires[i], rof: value };
                }
            }
            return { ...prev, tires: newTires };
        });
    }, [tireTypes, tireManufacturers, setFormValues]);

    const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement>, id: string) => {
        event.stopPropagation();
        event.preventDefault();
        setTireId(id);
        setOpenDialog(true);
    };

    const handleDeleteDialogConfirm = useCallback(async () => {
        if (!tireId) return;
        try {
            const deleted = await deleteFleetTireAPI(tireId, t);
            if (deleted && formValues.id) {
                await fetchTireSet(formValues.id);
            } else {
                setFormValues((prev) => ({ ...prev, tires: prev.tires.filter(i => i.id !== tireId) }));
            }
            setOpenDialog(false);
            setTireId("");
        } catch { /* */ }
    }, [tireId, t]);

    const handleDeleteDialogClose = useCallback(() => {
        setTireId("");
        setOpenDialog(false);
    }, []);

    const handleTireAddSuccess = useCallback((newTires: FleetTireAddRow[]) => {
        setFormValues((prev) => ({ ...prev, tires: [...prev.tires, ...newTires] }));
        setShowTireAddModal(false);
        if (setErrors) setErrors({});
    }, [setFormValues]);

    return (
        <>
            {formValues.tires && formValues.tires.length > 0 ? (
                <div className="pb-4">
                    {remaining > 0 && (
                        <div className="flex justify-end mb-4">
                            <Button
                                variant="outlined"
                                children={t("admin.fleet.tire.set.add.tiretoset")}
                                onClick={() => setShowTireAddModal(true)}
                                startIcon="/media/icons/plus.svg"
                                size="small"
                            />
                        </div>
                    )}
                    <TireTable
                        tires={formValues.tires}
                        groups={groups}
                        allSameDimensions={allSameDimensions}
                        dirtyFields={dirtyFields}
                        tireTypeOptions={tireTypeOptions}
                        manufacturerOptions={manufacturerOptions}
                        onGroupChange={handleGroupChange}
                        onDeleteClick={handleDeleteClick}
                    />
                    {errors?.["tires"] && (
                        <p className="text-center text-sm text-red-600 mt-3">{errors["tires"]}</p>
                    )}
                </div>
            ) : (
                <TireEmptyState
                    onAdd={() => setShowTireAddModal(true)}
                    error={errors?.["tires"]}
                />
            )}

            <TireAddModal
                show={showTireAddModal}
                onSuccess={handleTireAddSuccess}
                onClose={() => setShowTireAddModal(false)}
                modalSize="small"
                vehicleId={formValues.vehicle?.id}
                fromTireSet
                maxQuantity={remaining}
            />
            <Dialog
                open={openDialog}
                title={t("admin.fleet.vehicle.tire.dialog.delete.title")}
                message={t("admin.fleet.vehicle.tire.dialog.delete.message")}
                confirmText={t("admin.fleet.vehicle.tire.dialog.delete.confirmtext")}
                cancelText={t("admin.fleet.vehicle.tire.dialog.delete.canceltext")}
                onConfirm={handleDeleteDialogConfirm}
                onCancel={handleDeleteDialogClose}
            />
        </>
    );
};

export default memo(TireRow);
